import { studyQueryKeys } from '@/config/queryKeys';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { CollapsibleTableRef, SampleTablesPanelGroup } from '@/pages/Study/components/SampleTablesPanelGroup';
import { StudyInfoSection } from '@/pages/Study/components/StudyInfoSection';
import { StudyTitleBar } from '@/pages/Study/components/StudyTitleBar';
import { fetchStudy } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import { Study } from '@/types';
import { FullLoading } from '@components/index';
import { Stack, StackProps } from '@nautilusbio/nb-design-system/Stack';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createLazyFileRoute } from '@tanstack/react-router';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDefaultLayout, usePanelRef } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';

import { canUserEditStudy } from '../../config/studies/editInPlace';
import studyStyles from '../../pages/Studies/styles.module.css';
import '../../styles/components/ag-grid-overrides.css';
import pageStyles from '../../styles/pages/index.module.css';

export const Route = createLazyFileRoute('/studies/$studyId')({
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('studies');

    return <FullLoading label={`${t('fetching')}...`} />;
  },
});

const stackSharedProps: Partial<StackProps> = {
  alignItems: 'center',
  // Use design system helpers if needed, otherwise keep minimal
  orientation: 'horizontal',
};
const minPanelSize = '47px';

function RouteComponent() {
  const { t } = useTranslation('studies');
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));
  const { studyId } = Route.useParams();
  const { user } = useAuthStatus();

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: `study-${studyId}-tables-layout`,
    panelIds: ['proteoformCount', 'proteoforms'],
    storage: localStorage,
  });

  const proteoformCountRef = usePanelRef();
  const proteoformsRef = usePanelRef();

  // Table refs for imperative updateCollapsed
  const proteoformCountsTableRef = useRef<CollapsibleTableRef>(null);
  const proteoformsTableRef = useRef<CollapsibleTableRef>(null);

  // Early return if no organization selected
  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error(t('study.no_org_selected'))} />;
  }

  // Extract orgId after guard for type safety and DRY
  const orgId = selectedOrg.id;
  const queryKey = studyQueryKeys.detail(orgId, studyId);

  const { data, isLoading } = useSuspenseQuery<Study>({
    queryKey,
    queryFn: () => fetchStudy(orgId, studyId),
  });

  // Check if current user can edit this study
  const canEdit = canUserEditStudy(data, user);

  // Check if current user is owner
  const isOwner = useMemo(() => data?.allowed_users?.some(u => u.id === user?.id && u.is_owner) || false, [data, user]);

  const emptyTxt = t('empty', { ns: 'general' });

  return (
    <Stack className={studyStyles.container} fullWidth orientation="vertical">
      <StudyTitleBar
        studyStyles={studyStyles}
        pageStyles={pageStyles}
        t={t}
        data={data}
        orgId={orgId}
        studyId={studyId}
        isOwner={isOwner}
      />

      <StudyInfoSection
        orgId={orgId}
        studyId={studyId}
        data={data}
        canEdit={canEdit}
        isLoading={isLoading}
        t={t}
        emptyTxt={emptyTxt}
        stackSharedProps={stackSharedProps}
      />

      <SampleTablesPanelGroup
        orgId={orgId}
        studyId={studyId}
        numSamples={data?.num_samples ?? 0}
        numInstrumentRuns={data?.num_instrument_runs ?? 0}
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
        proteoformCountRef={proteoformCountRef}
        proteoformsRef={proteoformsRef}
        proteoformCountsTableRef={proteoformCountsTableRef}
        proteoformsTableRef={proteoformsTableRef}
        minPanelSize={minPanelSize}
        isLoading={isLoading}
      />
    </Stack>
  );
}
